import { localMessage } from '../i18n/apiErrors';

export function readSession(key, fallback = null) {
  try { return JSON.parse(sessionStorage.getItem(key)) ?? fallback; }
  catch { return fallback; }
}

export function writeSession(key, value) {
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch { /* Le stockage désactivé ne doit pas bloquer une commande. */ }
}

export async function readResponse(response, fallback) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = data?.detail;
    const message = typeof detail === 'string' ? detail : Array.isArray(detail)
      ? detail.map(error => error.msg).filter(Boolean).join(' · ') : '';
    throw new Error(localMessage(message || fallback));
  }
  if (data == null) throw new Error(localMessage(fallback));
  return data;
}

// Une connexion bloquée doit permettre une reprise, sans renouveler l'intention de paiement.
export async function fetchCheckoutResponse(url, options = {}, fallback = 'Le service est momentanément indisponible.') {
  const controller = new AbortController();
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(localMessage('La connexion prend trop de temps. Vérifiez votre connexion et réessayez. Votre panier est conservé.')));
      controller.abort();
    }, 20000);
  });
  try {
    return await Promise.race([
      fetch(url, { ...options, signal: controller.signal }).then(response => readResponse(response, fallback)),
      deadline,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export function checkoutItems(items) {
  return items.map(i => ({
    product_slug: i.slug,
    product_name: i.name,
    color: i.color,
    price: i.price,
    quantity: i.quantity,
    image: i.image,
    configuration_id: i.configurationId,
    configuration: i.configuration,
  }));
}

const INTENTS_KEY = 'lpad_checkout_intents';
const checkoutUnavailable = 'Le paiement est indisponible. Votre panier a été conservé.';

// Même contenu, même intention, indépendamment de l'ordre des propriétés JSON.
function stableJSON(value) {
  return JSON.stringify(value, (_, entry) => entry && typeof entry === 'object' && !Array.isArray(entry)
    ? Object.fromEntries(Object.keys(entry).sort().map(key => [key, entry[key]])) : entry);
}

function checkoutURL(value) {
  try {
    const url = new URL(value);
    if (url.protocol === 'https:') return url.href;
  } catch { }
  throw new Error(localMessage('Le lien de paiement est indisponible. Votre panier a été conservé.'));
}

function newCheckoutKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `lpad-${Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

function savedIntents() {
  const saved = readSession(INTENTS_KEY, []);
  return Array.isArray(saved) ? saved.filter(intent => intent && typeof intent.key === 'string'
    && /^[\x21-\x7e]{1,200}$/.test(intent.key) && intent.payload && typeof intent.signature === 'string') : [];
}

function saveIntent(intent, retiredKey) {
  const intents = savedIntents().filter(entry => entry.key !== intent.key && entry.key !== retiredKey);
  intents.push(intent);
  writeSession(INTENTS_KEY, intents);
  // Sans persistance, un rechargement après un timeout risquerait de créer une autre commande.
  if (JSON.stringify(readSession(INTENTS_KEY)) !== JSON.stringify(intents)) {
    throw new Error(localMessage('Activez le stockage de session de votre navigateur pour préparer le paiement en toute sécurité.'));
  }
}

export async function prepareCheckout({ payload, userEmail = null, token, api = '' }) {
  const request = JSON.parse(JSON.stringify(payload));
  const signature = stableJSON({ request, userEmail });
  const paid = readSession('lpad_paid_sessions', []);
  const matches = entry => entry.signature === signature
    && !(Array.isArray(paid) && paid.includes(entry.sessionId));
  let intent = savedIntents().find(matches);
  let retiredKey;
  const statusFor = sessionId => fetchCheckoutResponse(
    `${api}/api/orders/status/${encodeURIComponent(sessionId)}`, {},
    'Impossible de vérifier le paiement précédent. Réessayez la vérification avant de payer.');
  const confirmation = sessionId => ({ sessionId, confirmation: true });

  // Ancien checkout sans clé : on ne peut pas recréer sa session sans risquer un doublon.
  const pending = readSession('lpad_checkout_pending');
  if (!intent && pending?.sessionId && !pending.idempotencyKey) {
    const status = await statusFor(pending.sessionId);
    if (paymentState(status) !== 'failed') return confirmation(pending.sessionId);
    writeSession('lpad_checkout_pending', null);
  }

  if (intent?.sessionId) {
    const status = await statusFor(intent.sessionId);
    if (status.payment_status === 'paid') return confirmation(intent.sessionId);
    if (paymentState(status) === 'failed') {
      // Seule une fin vérifiée autorise une nouvelle tentative avec une nouvelle clé.
      retiredKey = intent.key;
      intent = null;
    } else if (status.status === 'open' && status.payment_status === 'unpaid') {
      if (intent.checkoutUrl) {
        const checkoutUrl = checkoutURL(intent.checkoutUrl);
        writeSession('lpad_checkout_pending', { sessionId: intent.sessionId, orderId: intent.orderId,
          checkoutUrl, idempotencyKey: intent.key });
        return { checkoutUrl, sessionId: intent.sessionId, orderId: intent.orderId };
      }
    } else {
      return confirmation(intent.sessionId);
    }
  }

  if (!intent) {
    intent = savedIntents().find(entry => entry.key !== retiredKey && matches(entry))
      || { key: newCheckoutKey(), signature, payload: request };
  }
  // Historique conservé : revenir à un panier antérieur reprend aussi son intention incertaine.
  saveIntent(intent, retiredKey);
  const headers = { 'Content-Type': 'application/json', 'Idempotency-Key': intent.key };
  if (token) headers.Authorization = `Bearer ${token}`;
  const data = await fetchCheckoutResponse(`${api}/api/orders/checkout`, {
    method: 'POST', headers, body: JSON.stringify(intent.payload),
  }, checkoutUnavailable);
  if (typeof data.session_id !== 'string' || !data.session_id
    || typeof data.order_id !== 'string' || !data.order_id) throw new Error(localMessage(checkoutUnavailable));
  const checkoutUrl = checkoutURL(data.checkout_url);
  Object.assign(intent, { sessionId: data.session_id, orderId: data.order_id, checkoutUrl });
  saveIntent(intent);
  writeSession('lpad_checkout_pending', { sessionId: intent.sessionId, orderId: intent.orderId,
    checkoutUrl, idempotencyKey: intent.key });
  return { checkoutUrl, sessionId: intent.sessionId, orderId: intent.orderId };
}

export function paymentState(data) {
  if (data.payment_status === 'paid') return Array.isArray(data.order?.items) && data.order.items.length ? 'success' : 'pending';
  if (['expired', 'failed', 'payment_failed', 'cancelled'].includes(data.payment_status)
    || ['expired', 'failed', 'payment_failed', 'cancelled'].includes(data.status)) return 'failed';
  return 'pending';
}
